import { useState, useEffect, useCallback } from 'react';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

type Category = 'internal-eng' | 'user-agents';

interface AgentEntry {
  ref: string;
  name: string;
  sourceLocation: string;
  tags: string[];
  description: string;
  displayName: string;
  summary: string;
  capabilities: string[];
  useCases: string[];
  proTip: string;
  minAiTier: number | null;
  icon: string;
  repo: string;
}

export interface AgentOutput {
  name: string;
  sourceLocation: string;
  description: string;
  displayName: string;
  summary: string;
  capabilities: string[];
  useCases: string[];
  proTip: string;
  minAiTier: number | null;
  icon: string;
  isA2a: boolean;
  repo: string;
}

export const BulkAgentSelectorField = ({
  onChange,
  rawErrors,
  schema,
  formData,
}: FieldExtensionComponentProps<AgentOutput[]>) => {
  const catalogApi = useApi(catalogApiRef);
  const [allAgents, setAllAgents] = useState<AgentEntry[]>([]);
  const [category, setCategory] = useState<Category>('internal-eng');
  const [loading, setLoading] = useState(true);

  const selected: AgentOutput[] = formData ?? [];

  // Fetch all Component entities once on mount
  useEffect(() => {
    let cancelled = false;
    catalogApi
      .getEntities({ filter: { kind: 'Component' } })
      .then(response => {
        if (cancelled) return;
        setAllAgents(
          response.items
            .map(e => ({
              ref: `component:default/${e.metadata.name}`,
              name: e.metadata.name,
              sourceLocation: (e.metadata.annotations?.['backstage.io/source-location'] ?? ''),
              tags: (e.metadata.tags as string[]) ?? [],
              description: (e.metadata.description as string) ?? '',
              displayName: ((e.spec as any)?.displayName as string) ?? '',
              summary: ((e.spec as any)?.summary as string) ?? '',
              capabilities: ((e.spec as any)?.capabilities as string[]) ?? [],
              useCases: ((e.spec as any)?.useCases as string[]) ?? [],
              proTip: ((e.spec as any)?.proTip as string) ?? '',
              minAiTier: ((e.spec as any)?.minAiTier as number) ?? null,
              icon: ((e.spec as any)?.icon as string) ?? '',
              repo: (e.metadata.annotations?.['github.com/project-slug'] ?? ''),
            }))
            .sort((a, b) => a.name.localeCompare(b.name)),
        );
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [catalogApi]);

  const filtered = allAgents.filter(a => a.tags.includes(category));

  const selectedNames = new Set(selected.map(s => s.name));

  const handleCategoryChange = useCallback(
    (newCategory: Category) => {
      setCategory(newCategory);
      onChange([]);
    },
    [onChange],
  );

  const toggle = useCallback(
    (agent: AgentEntry) => {
      const next = selectedNames.has(agent.name)
        ? selected.filter(s => s.name !== agent.name)
        : [...selected, {
            name: agent.name,
            sourceLocation: agent.sourceLocation,
            description: agent.description,
            displayName: agent.displayName,
            summary: agent.summary,
            capabilities: agent.capabilities,
            useCases: agent.useCases,
            proTip: agent.proTip,
            minAiTier: agent.minAiTier,
            icon: agent.icon,
            isA2a: agent.tags.includes('a2a'),
            repo: agent.repo,
          }];
      onChange(next);
    },
    [selected, selectedNames, onChange],
  );

  const toggleAll = useCallback(() => {
    onChange(
      selected.length === filtered.length
        ? []
        : filtered.map(a => ({
            name: a.name,
            sourceLocation: a.sourceLocation,
            description: a.description,
            displayName: a.displayName,
            summary: a.summary,
            capabilities: a.capabilities,
            useCases: a.useCases,
            proTip: a.proTip,
            minAiTier: a.minAiTier,
            icon: a.icon,
            isA2a: a.tags.includes('a2a'),
            repo: a.repo,
          })),
    );
  }, [selected.length, filtered, onChange]);

  if (loading) {
    return <FormHelperText>Loading agents...</FormHelperText>;
  }

  return (
    <FormControl margin="normal" fullWidth>
      <FormHelperText>{schema.title || 'Agents to Deploy'}</FormHelperText>

      <Select
        value={category}
        onChange={e => handleCategoryChange(e.target.value as Category)}
        style={{ marginBottom: 8 }}
      >
        <MenuItem value="internal-eng">Internal Eng Agents</MenuItem>
        <MenuItem value="user-agents">User Agents</MenuItem>
      </Select>

      {filtered.length > 0 && (
        <>
          <FormControlLabel
            control={
              <Checkbox
                checked={selected.length === filtered.length}
                indeterminate={selected.length > 0 && selected.length < filtered.length}
                onChange={toggleAll}
              />
            }
            label={`Select All (${filtered.length})`}
          />
          <div style={{ maxHeight: 300, overflow: 'auto' }}>
            {filtered.map(agent => (
              <FormControlLabel
                key={agent.ref}
                control={
                  <Checkbox
                    checked={selectedNames.has(agent.name)}
                    onChange={() => toggle(agent)}
                    size="small"
                  />
                }
                label={agent.name}
                style={{ display: 'block' }}
              />
            ))}
          </div>
          <FormHelperText>{selected.length} of {filtered.length} selected</FormHelperText>
        </>
      )}

      {filtered.length === 0 && (
        <FormHelperText>No agents found for "{category}"</FormHelperText>
      )}

      {rawErrors?.length > 0 && (
        <FormHelperText error>{rawErrors[0]}</FormHelperText>
      )}
    </FormControl>
  );
};
